import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { VerticalType } from '@rma/generic/domain/types';
import { Environment, RmaUrlsService } from '@rma/generic/util/environment';
import {
  AggregateRating,
  Event,
  FinancialService,
  GeoCoordinates,
  Organization,
  PostalAddress,
  Rating,
  RealEstateAgent,
  Residence,
  Review,
  WithContext,
} from 'schema-dts';
import {
  AddressLinkingData,
  AgentLinkingData,
  EventLinkingData,
  LinkingData,
  LinkingDataTypes,
  ResidenceLinkingData,
  ReviewLinkingData,
} from '../domain';
import { typedEntries } from '../util-typed-entries/typed-entries';

@Injectable({
  providedIn: 'root',
})
export class LinkingDataService {
  public readonly schemaContext = 'https://schema.org';

  public constructor(
    private readonly environment: Environment,
    private readonly rmaUrls: RmaUrlsService,
    private readonly translationService: TranslocoService,
  ) {}

  public generateLdSchema(config?: LinkingData | null): LinkingDataTypes[] | undefined {
    return config
      ? typedEntries(config)
          .filter(([_, value]) => Boolean(value))
          .reduce((accum, [key, data]) => {
            const ld = this.handleType(key, data);
            return Array.isArray(ld) ? [...accum, ...ld] : [...accum, ld];
          }, [])
      : undefined;
  }

  private handleType(type: keyof LinkingData, data): LinkingDataTypes {
    switch (type) {
      case 'salesAgent':
        return this.buildAgent(data, 'RealEstateAgent');
      case 'financeAgent':
        return this.buildAgent(data, 'FinancialService');
      case 'events':
        return data.map((events) => this.buildEventSchemaWithContext(events));
      case 'residences':
        return data.map((residences) => this.buildResidenceSchemaWithContext(residences));
      case 'reviews':
        return data.map((reviews) => this.buildReviewSchemaWithContext(reviews));
      default:
        throw new Error(`LinkingDataService could not handle ${type}`);
    }
  }

  private buildUrl(url?: string): string | undefined {
    if (!url) {
      return undefined;
    }
    if (url.startsWith('http')) {
      return url;
    }
    return this.rmaUrls.baseUrl(url);
  }

  private buildAgent(
    data: AgentLinkingData,
    type: 'RealEstateAgent' | 'FinancialService',
  ): WithContext<RealEstateAgent | FinancialService> {
    return {
      '@context': this.schemaContext,
      '@type': type,
      name: data.name,
      url: this.buildUrl(data.url),
      logo: data.logo,
      telephone: data.telephone,
      review: data.reviews?.map((r) => this.buildReviewSchema(r)),
      address: this.buildPostalAddressSchema(data.address),
      image: data.image,
      priceRange: data.priceRange,
      aggregateRating: this.buildReviewAggregateSchema(data.aggregateRating),
      sameAs: this.undefinedIfEmptyArray(data.sameAs ?? []),
      areaServed: this.undefinedIfEmptyArray(data.areaServed ?? []),
      awards: this.undefinedIfEmptyArray(data.awards ?? []),
    };
  }

  private undefinedIfEmptyArray(array: string[]) {
    const cleanedArray = array.filter((s) => Boolean(s));
    if (cleanedArray.length) {
      return cleanedArray;
    }

    return undefined;
  }

  private buildReviewAggregateSchema(review?: { rating?: number; reviewCount?: number }): AggregateRating | undefined {
    return !review || !review.rating || !review.reviewCount
      ? undefined
      : {
          '@type': 'AggregateRating',
          ratingValue: review.rating,
          bestRating: 5,
          worstRating: 1,
          reviewCount: review.reviewCount,
        };
  }

  private buildPostalAddressSchema(address?: AddressLinkingData): PostalAddress | undefined {
    return address
      ? {
          '@type': 'PostalAddress',
          streetAddress: address.streetAddress,
          addressLocality: address.addressLocality,
          addressRegion: address.addressRegion,
          postalCode: address.postalCode,
          addressCountry: address.addressCountry,
        }
      : undefined;
  }

  private buildEventSchemaWithContext(event: EventLinkingData): WithContext<Event> {
    return {
      '@context': this.schemaContext,
      '@type': 'Event',
      location: this.buildResidenceSchemaWithContext(event.residence),
      name: event.eventType,
      startDate: event.eventDate.toISOString(),
      url: this.buildUrl(event.canonicalUrl),
    };
  }

  private buildResidenceSchemaWithContext(residence: ResidenceLinkingData): WithContext<Residence> {
    return {
      '@context': this.schemaContext,
      '@type': 'Residence',
      image: residence.imageUrl,
      description: residence.description,
      geo: this.buildGeoCoordinates(residence.address),
      name: residence.address?.streetAddress,
      address: this.buildPostalAddressSchema(residence.address),
    };
  }

  private buildReviewSchemaWithContext(review: ReviewLinkingData): WithContext<Review> | undefined {
    const reviewLd = this.buildReviewSchema(review);
    return reviewLd ? { '@context': this.schemaContext, ...reviewLd } : reviewLd;
  }

  private buildReviewSchema(review: ReviewLinkingData): Review {
    const itemReviewed = this.itemReviewed(review, this.buildUrl(review.agent.profileUrl) ?? '');

    const reviewRating: Rating = {
      '@type': 'Rating',
      ratingValue: review.rating ?? 0,
      bestRating: 5,
      worstRating: 0,
    };

    const userType = this.getReviewerType(review.userType ?? 'Unknown');

    return {
      '@type': 'Review',
      datePublished: review.reviewDate?.toISOString(),
      headline: review.title,
      itemReviewed,
      publisher: this.getPublisherInfo(this.environment),
      reviewBody: review.description,
      reviewRating,
      author: {
        '@type': 'Person',
        name: (review.reviewerName ?? userType)?.trim() || userType,
      },
    };
  }

  private buildGeoCoordinates(coords?: { latitude?: number; longitude?: number }): GeoCoordinates | undefined {
    if (!coords || !coords.latitude || !coords.longitude) {
      return undefined;
    }

    return {
      '@type': 'GeoCoordinates',
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
  }

  private getReviewerType(userType: string): string {
    return this.translationService.translate(`reviewer-type.${userType.toLowerCase()}`);
  }

  private getPublisherInfo(environment: Environment): Organization {
    return {
      '@type': 'Organization',
      name: 'RateMyAgent',
      url: environment.baseUrl,
      logo: `${environment.baseUrl}/assets/images/logo-black.png`,
      description:
        "ratemyagent.com.au is Australia's leading real estate agent and agency review website, helping sellers make informed decisions about choosing an estate agent.",
      sameAs: [environment.social.facebookUrl, environment.social.twitterUrl, environment.social.linkedinUrl],
    };
  }

  // this should be converted to agent
  private itemReviewed(review: ReviewLinkingData, agentProfileUrl: string): FinancialService | RealEstateAgent {
    return {
      '@type': review.type === VerticalType.Finance ? 'FinancialService' : 'RealEstateAgent',
      // should we have this on the other one?
      '@id': agentProfileUrl,
      name: review.agent.name,
      url: agentProfileUrl,
      image: review.agent.avatarUrl,
      telephone: review.agent.phone,
      address: this.buildPostalAddressSchema(review.agent.agencyAddress),
    };
  }
}
